import { Check, MagnifyingGlass, Plus } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { EmptyState } from "@tokenterminal/core/ui/messaging/emptyState/EmptyState"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { useMemo } from "react"
import { NON_STATIC_ITEMS, STATIC_ITEMS } from "./constants"

const SelectableGroup: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="flex flex-col">{children}</div>
)

const SelectableItemColumn: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <div className="flex flex-col border border-solid border-border-default overflow-hidden rounded-lg">
    {children}
  </div>
)

const SelectableGroupHeader: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <div className="px-1 pt-2 pb-2">
    <div className="text-xs text-fg-secondary">{children}</div>
  </div>
)

const SelectableItem = ({
  item,
  onSelect,
  isAdded,
}: {
  item: { field: string; header?: string; label?: string }
  onSelect: () => void
  isAdded: boolean
}) => {
  return (
    <button
      className="group/selectable-item flex items-center justify-between gap-2.5 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-900 px-3 py-2 w-full border-b border-solid border-neutral-100 dark:border-neutral-800 last:border-b-0"
      onClick={onSelect}
    >
      {item.header || item.label}
      <div className="relative w-[80px] flex items-center justify-end h-4">
        <Icon
          as={Plus}
          className="w-4 h-4 text-fg-secondary opacity-0 group-hover/selectable-item:opacity-100 absolute"
        />
        {isAdded && (
          <div className="flex items-center gap-2 absolute top-0 right-0 text-xs text-fg-secondary group-hover/selectable-item:opacity-0">
            Added
            <Icon as={Check} className="w-3 h-3" />
          </div>
        )}
      </div>
    </button>
  )
}

export const SelectableColumnsList = ({
  groupedColumns,
  ungroupedColumns,
  search,
  handleAddColumn,
  draftSelectedColumns,
}: {
  ungroupedColumns: {
    field: string
    header?: string
    label?: string
  }[]
  groupedColumns: {
    label: string
    value: string
    children: {
      label: string
      value: string
      metric_id: string
      keywords?: string[]
      static: boolean
    }[]
  }[]
  search: string
  handleAddColumn: (field: string) => void
  draftSelectedColumns: PresetColumnConfig[]
}) => {
  const filteredUngroupedColumns = useMemo(() => {
    return ungroupedColumns.filter((item) =>
      item.header?.toLowerCase().includes(search.toLowerCase())
    )
  }, [ungroupedColumns, search])

  const filteredGroupedColumns = useMemo(() => {
    return groupedColumns.filter((item) =>
      item.children.some((child) =>
        child.label.toLowerCase().includes(search.toLowerCase())
      )
    )
  }, [groupedColumns, search])

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="flex flex-col gap-4">
        {filteredUngroupedColumns.length > 0 && (
          <SelectableItemColumn>
            {filteredUngroupedColumns.map((item) => (
              <SelectableItem
                key={item.field}
                item={item}
                onSelect={() => {
                  handleAddColumn(item.field)
                }}
                isAdded={draftSelectedColumns.some(
                  (c) => c.field === item.field
                )}
              />
            ))}
          </SelectableItemColumn>
        )}
        {filteredGroupedColumns.map((item) => (
          <SelectableGroup key={item.label}>
            <SelectableGroupHeader>{item.label}</SelectableGroupHeader>
            <SelectableItemColumn>
              {item.children
                .filter((child) =>
                  child.label.toLowerCase().includes(search.toLowerCase())
                )
                .map((child) => (
                  <SelectableItem
                    key={child.value}
                    item={{
                      field: `${child.metric_id}_${child.static ? STATIC_ITEMS[0].value : NON_STATIC_ITEMS[0].value}`,
                      label: child.label,
                    }}
                    onSelect={() => {
                      handleAddColumn(
                        `${child.metric_id}_${child.static ? STATIC_ITEMS[0].value : NON_STATIC_ITEMS[0].value}`
                      )
                    }}
                    // TODO: Update this to handle metric columns
                    isAdded={draftSelectedColumns.some((c) =>
                      c.field.includes(child.metric_id)
                    )}
                  />
                ))}
            </SelectableItemColumn>
          </SelectableGroup>
        ))}
        {filteredUngroupedColumns.length === 0 &&
          filteredGroupedColumns.length === 0 && (
            <EmptyState
              headingText="No columns found"
              bodyText="Try a different search"
              icon={MagnifyingGlass}
              size="small"
            />
          )}
      </div>
    </div>
  )
}
